import {
  Box,
  RangeSlider,
  RangeSliderTrack,
  RangeSliderFilledTrack,
  RangeSliderThumb,
  RangeSliderMark,
  Tooltip,
} from "@chakra-ui/react";
import { SEND_CONFIG_THEME } from "./theme";

interface SliderMark {
  value: number;
  label: string;
}

interface ConfigRangeSliderProps {
  min: number;
  max: number;
  step: number;
  value: [number, number];
  marks: SliderMark[];
  formatLabel: (value: number) => string;
  onChange: (value: [number, number]) => void;
  onChangeEnd: (value: [number, number]) => void;
  valueInMinutes?: [number, number]; // Optional: actual minute values for tooltip (when using index-based slider)
}

/**
 * Reusable range slider component with consistent styling
 * Used for both advance time and day period sliders
 */
export function ConfigRangeSlider({
  min,
  max,
  step,
  value,
  marks,
  formatLabel,
  onChange,
  onChangeEnd,
  valueInMinutes,
}: ConfigRangeSliderProps) {
  // Use valueInMinutes for tooltip if provided (index-based slider), otherwise use value directly
  const displayValues = valueInMinutes || value;
  return (
    <Box
      pb={SEND_CONFIG_THEME.spacing.sliderPaddingBottom}
      px={SEND_CONFIG_THEME.spacing.sliderPaddingX}
    >
      <RangeSlider
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={onChange}
        onChangeEnd={onChangeEnd}
        colorScheme="blue"
      >
        {marks.map((mark) => (
          <RangeSliderMark
            key={mark.value}
            value={mark.value}
            mt={2}
            ml={mark.value === min ? 0 : -2}
            fontSize={SEND_CONFIG_THEME.fontSize.mark}
            color={SEND_CONFIG_THEME.colors.textSecondary}
            sx={{
              textAlign: "center",
              whiteSpace: "nowrap",
              width: "35px",
            }}
          >
            {mark.label}
          </RangeSliderMark>
        ))}

        <RangeSliderTrack bg={SEND_CONFIG_THEME.colors.trackBg}>
          <RangeSliderFilledTrack bg={SEND_CONFIG_THEME.colors.primary} />
        </RangeSliderTrack>

        <Tooltip
          hasArrow
          bg={SEND_CONFIG_THEME.colors.primary}
          color="white"
          placement="top"
          label={formatLabel(displayValues[0])}
          fontSize={SEND_CONFIG_THEME.fontSize.tooltip}
          py={1}
          px={2}
        >
          <RangeSliderThumb
            index={0}
            boxSize={SEND_CONFIG_THEME.slider.thumbSize}
            borderWidth={SEND_CONFIG_THEME.slider.borderWidth}
            borderColor={SEND_CONFIG_THEME.colors.primary}
          />
        </Tooltip>

        <Tooltip
          hasArrow
          bg={SEND_CONFIG_THEME.colors.primary}
          color="white"
          placement="top"
          label={formatLabel(displayValues[1])}
          fontSize={SEND_CONFIG_THEME.fontSize.tooltip}
          py={1}
          px={2}
        >
          <RangeSliderThumb
            index={1}
            boxSize={SEND_CONFIG_THEME.slider.thumbSize}
            borderWidth={SEND_CONFIG_THEME.slider.borderWidth}
            borderColor={SEND_CONFIG_THEME.colors.primary}
          />
        </Tooltip>
      </RangeSlider>
    </Box>
  );
}
